/**
 * Yoink Adventures V2 - Utilities
 *
 * Pure utility functions with NO external dependencies.
 * No Supabase, no state access - just pure functions.
 */

// =============================================================================
// STRING / HTML
// =============================================================================

/**
 * Escape HTML entities to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Fold a region name (country/state/county) to a comparison key: case,
 * diacritics, whitespace runs, and a leading "The " ignored. Google
 * returns these names in whatever language/accent form each lookup
 * happened to use ("Bolívar" vs "Bolivar", "The Bahamas" vs "Bahamas"),
 * and legacy rows mix them, so exact-string grouping splits one real
 * region into two.
 * @param {string} name - Region name (null/undefined tolerated)
 * @returns {string} Folded key ('' for empty input)
 */
export function foldRegionName(name) {
  return (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^the /, '');
}

/**
 * Whether a location row is in the United States. The "states" count
 * (geography summary + State Collector badge) is US-states-only \u2014 the
 * top badge tier means all 50 actual US states, not a worldwide mix of
 * provinces. Prefers country_code when the row carries one (the profile
 * feed embed doesn't); falls back to the folded country name, covering
 * the legacy spellings geocoding produced before COUNTRY_NAME_MAP.
 * @param {object} loc - locations row ({country_code, country})
 * @returns {boolean}
 */
export function isUnitedStates(loc) {
  if (!loc) return false;
  if (loc.country_code) return String(loc.country_code).toUpperCase() === 'US';
  const folded = foldRegionName(loc.country);
  return folded === 'united states' || folded === 'usa' || folded === 'united states of america';
}

/**
 * Escape HTML, then turn any http(s) URLs into clickable links.
 * Escaping happens first so the input is XSS-safe; the URL match runs
 * against already-escaped text and only emits anchors we build ourselves.
 * @param {string} str - Plain text that may contain URLs
 * @returns {string} HTML-safe string with URLs wrapped in <a> tags
 */
export function linkifyText(str) {
  const escaped = escapeHtml(str);
  // URLs in the escaped string can't contain <, >, " or ' (they became entities),
  // so a simple run-of-non-space match is safe. Trailing punctuation is excluded.
  return escaped.replace(/https?:\/\/[^\s<]+[^\s<.,;:!?)'"]/g, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
}

/**
 * Format a date for display
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export function formatDate(date, options = {}) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return d.toLocaleDateString(undefined, defaultOptions);
}

/**
 * Get relative time string ("2 hours ago")
 * @param {Date|string} date - Date to compare
 * @returns {string} Relative time string
 */
export function getTimeAgo(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek === 1 ? '' : 's'} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
  return `${diffYear} year${diffYear === 1 ? '' : 's'} ago`;
}

/**
 * Truncate string with ellipsis
 * @param {string} str - String to truncate
 * @param {number} maxLen - Maximum length
 * @returns {string} Truncated string
 */
export function truncate(str, maxLen = 100) {
  if (!str || str.length <= maxLen) return str || '';
  return str.slice(0, maxLen - 3) + '...';
}

/**
 * Strip HTML tags from a string, returning plain text
 * @param {string} html - HTML string to strip
 * @returns {string} Plain text
 */
export function stripHtmlTags(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

/**
 * Sanitize HTML to allow only safe tags/attributes for rich text display.
 * Prevents XSS while allowing formatting from the rich text editor.
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;

  const ALLOWED_TAGS = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
    'h1', 'h2', 'h3', 'ol', 'ul', 'li', 'a', 'blockquote',
    'pre', 'code', 'sub', 'sup', 'span'
  ]);
  const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'class']);

  function cleanNode(node) {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) { // Element
        if (!ALLOWED_TAGS.has(child.tagName.toLowerCase())) {
          // Replace disallowed element with its text content
          const text = document.createTextNode(child.textContent);
          node.replaceChild(text, child);
        } else {
          // Remove disallowed attributes
          const attrs = Array.from(child.attributes);
          for (const attr of attrs) {
            if (!ALLOWED_ATTRS.has(attr.name)) {
              child.removeAttribute(attr.name);
            }
          }
          if (child.tagName.toLowerCase() === 'a') {
            // Only http/https/mailto hrefs survive — anything else
            // (javascript:, data:, vbscript:, …) is stored XSS waiting
            // for a click. Relative hrefs resolve against the origin
            // (http/https) and pass.
            let safeHref;
            try {
              const protocol = new URL(child.getAttribute('href') || '', window.location.origin).protocol;
              safeHref = protocol === 'http:' || protocol === 'https:' || protocol === 'mailto:';
            } catch {
              safeHref = false;
            }
            if (!safeHref) child.removeAttribute('href');
            // Force links to open in new tab safely
            child.setAttribute('target', '_blank');
            child.setAttribute('rel', 'noopener noreferrer');
          }
          cleanNode(child);
        }
      }
    }
  }

  cleanNode(div);
  return div.innerHTML;
}

// =============================================================================
// ASYNC UTILITIES
// =============================================================================

/**
 * Debounce a function
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(fn, delay = 300) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Throttle a function
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Minimum time between calls
 * @returns {Function} Throttled function
 */
export function throttle(fn, limit = 300) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Sleep for a specified duration
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Re-export from shared modules for backward compatibility
export { withTimeout } from './shared/with-timeout.js';
export { getCategoryGradient } from './shared/category-gradients.js';

// =============================================================================
// IMAGE UTILITIES
// =============================================================================

/**
 * Compress an image file
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<Blob>} Compressed image blob
 */
export async function compressImage(file, maxWidth = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const objectUrl = URL.createObjectURL(file);

    // Without this, a file the browser can't decode (odd HEIC, corrupt
    // download, non-image) leaves the promise unsettled forever and the
    // caller's save hangs (M8.5 Tier 0).
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('That file isn’t an image this browser can read.'));
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Scale down if wider than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

/**
 * Get dimensions of an image file
 * @param {File} file - Image file
 * @returns {Promise<{width: number, height: number}>}
 */
export async function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

// =============================================================================
// UI FEEDBACK
// =============================================================================

// Toast container reference
let toastContainer = null;

/**
 * Ensure toast container exists
 */
function ensureToastContainer() {
  if (!toastContainer) {
    toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'toast-container';
      toastContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 8px;
        pointer-events: none;
      `;
      document.body.appendChild(toastContainer);
    }
  }
  return toastContainer;
}

/**
 * Show a toast notification
 * @param {string} message - Message to display
 * @param {string} type - 'info' | 'success' | 'warning' | 'error'
 * @param {number} duration - Duration in ms (default: 3000)
 */
export function showToast(message, type = 'info', duration = 3000) {
  const container = ensureToastContainer();

  const colors = {
    info: '#795548',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444'
  };

  const toast = document.createElement('div');
  toast.style.cssText = `
    background: ${colors[type] || colors.info};
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-family: system-ui, sans-serif;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    pointer-events: auto;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.3s, transform 0.3s;
  `;
  toast.textContent = message;

  container.appendChild(toast);

  // Trigger animation
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });

  // Remove after duration
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(-20px)';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

/**
 * Show an error toast
 * @param {string} message - Error message
 */
export function showErrorToast(message) {
  showToast(message, 'error', 4000);
}

/**
 * Show a success toast
 * @param {string} message - Success message
 */
export function showSuccessToast(message) {
  showToast(message, 'success');
}

// Screen reader announcement element
let srAnnouncer = null;

/**
 * Announce a message to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - 'polite' | 'assertive'
 */
export function announceToScreenReader(message, priority = 'polite') {
  if (!srAnnouncer) {
    srAnnouncer = document.createElement('div');
    srAnnouncer.setAttribute('aria-live', priority);
    srAnnouncer.setAttribute('aria-atomic', 'true');
    srAnnouncer.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(srAnnouncer);
  }

  // Update aria-live if priority changed
  srAnnouncer.setAttribute('aria-live', priority);

  // Clear and set message (triggers announcement)
  srAnnouncer.textContent = '';
  setTimeout(() => {
    srAnnouncer.textContent = message;
  }, 100);
}

// =============================================================================
// DOM UTILITIES
// =============================================================================

/**
 * Query selector shorthand
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element|null}
 */
export function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Query selector all shorthand
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element[]}
 */
export function $$(selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
}

/**
 * Create an element with attributes and children
 * @param {string} tag - Tag name
 * @param {object} attrs - Attributes object
 * @param {(string|Element)[]} children - Child elements or text
 * @returns {Element}
 */
export function createElement(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'className') {
      el.className = value;
    } else if (key === 'style' && typeof value === 'object') {
      Object.assign(el.style, value);
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === 'dataset' && typeof value === 'object') {
      Object.assign(el.dataset, value);
    } else {
      el.setAttribute(key, value);
    }
  }

  for (const child of children) {
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else if (child instanceof Element) {
      el.appendChild(child);
    }
  }

  return el;
}

/**
 * Remove all children from an element
 * @param {Element} el - Element to clear
 */
export function clearElement(el) {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}

// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Validate email format
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate password strength
 * @param {string} password
 * @returns {{valid: boolean, message: string}}
 */
export function validatePassword(password) {
  if (!password || password.length < 6) {
    return { valid: false, message: 'Password must be at least 6 characters' };
  }
  return { valid: true, message: '' };
}
